import { useMemo, useState } from 'react';
import { faCheck, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDrivingPolicyGetDrivingReports } from 'api/driving-policy/driving-policy';
import { ColumnSetting } from 'components/Table/utils';
import Button from 'components/inputs/Button';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import useUsers from 'contexts/basicData/useUsers';
import styled, { css, useTheme } from 'styled-components';
import toHoursAndMinutesString, {
  formatHoursAndMinutesString,
} from 'utils/time-helpers';
import Table from 'components/Table';
import { DrivingPolicyReportDto, TranslationLookups } from 'api/model';
import { getHoursAndMinutesFromMillisecondsString } from 'utils/date-helpers';
import {
  getDrivingBarHours,
  getProgressLevel,
  getProgressWidthPercent,
} from '../Availability/availability-helper';
import {
  DEFAULT_MAX_HOURS_PER_4_WEEKS,
  DEFAULT_MAX_HOURS_PER_WEEK,
} from 'constants/AppConstants';
import Input from 'components/inputs/Input';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import { useLocalStorage } from 'usehooks-ts';
import useBusinessUnits from 'contexts/basicData/useBusinessUnits';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 50px;
  padding: 10px;
  gap: 10px;
`;

const StyledMultiSelect = styled(MultiSelect)`
  padding: 6px;
  ${({ value, theme }) =>
    value.size > 0 &&
    css`
      background: ${theme.colors.background.highlight};
    `}
` as typeof MultiSelect;

export const RowCss = css`
  align-items: center;
  display: flex;
  padding: 2px 5px;
  &:hover {
    overflow: visible;
    // children of this row
    & > div {
      z-index: 10;
    }
  }
`;

export const ProgressContainer = styled.div`
  width: 100%; // Make the container fill the full width
  height: 100%; // Make the container fill the full height
  background: ${({ theme }) =>
    theme.colors.background.tertiary}; // Background for the unfilled part
`;

const getProgressColor = (widthPercent: number, theme: any) => {
  switch (getProgressLevel(widthPercent)) {
    case 'over':
      return theme.colors.foreground.error;
    case 'warning':
      return theme.colors.foreground.warning;
    default:
      return theme.colors.foreground.good;
  }
};

export const ProgressBar = styled.div<{ widthPercent: number }>`
  width: ${({ widthPercent }) => widthPercent}%;
  background: ${({ widthPercent, theme }) =>
    getProgressColor(widthPercent, theme)};
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0px 5px;
  color: ${({ theme, widthPercent }) =>
    widthPercent === 100
      ? theme.colors.foreground.button
      : theme.colors.foreground.primary};
`;

// Striped pattern for absence portion - same color but with diagonal stripes
const AbsenceStripes = styled.div<{ widthPercent: number }>`
  position: absolute;
  right: 0;
  top: 0;
  width: ${({ widthPercent }) => widthPercent}%;
  height: 100%;
  background: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 3px,
    rgba(0, 0, 0, 0.15) 3px,
    rgba(0, 0, 0, 0.15) 6px
  );
  pointer-events: none;
`;

const ProgressBarInner = styled.div<{ widthPercent: number }>`
  position: relative;
  width: ${({ widthPercent }) => widthPercent}%;
  background: ${({ widthPercent, theme }) =>
    getProgressColor(widthPercent, theme)};
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0px 5px;
  color: ${({ theme, widthPercent }) =>
    widthPercent === 100
      ? theme.colors.foreground.button
      : theme.colors.foreground.primary};
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:hover > div:last-child {
    display: block;
  }
`;

const TooltipPopup = styled.div`
  display: none;
  position: absolute;
  z-index: 100;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 3px;
  box-shadow: ${({ theme }) => theme.colors.shadow.dropdown};
  padding: 8px;
  top: 100%;
  left: 0;
  min-width: 150px;
  white-space: nowrap;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const TooltipLabel = styled.span`
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const TooltipValue = styled.span`
  font-weight: 500;
`;

interface ProgressBarWithTooltipProps {
  /** Arbetstiden i perioden så som körpolicyn räknar den — frånvarotiden inkluderad. */
  totalHours: number;
  /** Den del av totalHours som är frånvaro (8h per frånvarodag). */
  absenceHours: number;
  /** Taket för perioden (veckotak eller 4-veckorstak) som stapeln fylls mot. */
  maxHours: number;
  /**
   * Heltidsanställda får ingen frånvaro i stapeln alls: den varken breddar stapeln,
   * ingår i timtalet eller ritas som en streckad sektion. Frånvaron räknas fortfarande
   * som 8h arbete i körpolicyn — stapeln visar bara faktisk uppdragstid. Tooltipen med
   * Uppdrag/Frånvaro/Totalt utgår då också, eftersom stapeln inte längre innehåller
   * någon frånvaro att bryta ned.
   * (Backend lägger bara in frånvarotid för heltidsanställda, så timanställda har
   * ändå alltid absenceHours = 0. Villkoret ligger på rätt ställe om regeln breddas.)
   */
  isFullTime?: boolean;
}

export const ProgressBarWithTooltip: React.FC<ProgressBarWithTooltipProps> = ({
  totalHours,
  absenceHours,
  maxHours,
  isFullTime = false,
}) => {
  const theme = useTheme();
  const drivingHours = totalHours - absenceHours;
  // Bredd och timtal räknas fram ur samma värde, så att stapelns längd aldrig kan
  // motsäga siffran i den.
  const barHours = getDrivingBarHours(totalHours, absenceHours, isFullTime);
  const widthPercent = getProgressWidthPercent(barHours, maxHours);
  const barLabel = getHoursAndMinutesFromMillisecondsString(
    barHours * 1000 * 60 * 60
  );
  // Calculate what percentage of the filled bar is absence time
  const absencePercentOfBar =
    totalHours > 0
      ? Math.min(100, Math.max(0, (absenceHours / totalHours) * 100))
      : 0;

  if (isFullTime) {
    return (
      <ProgressContainer>
        <ProgressBarInner widthPercent={widthPercent}>
          {barLabel}
        </ProgressBarInner>
      </ProgressContainer>
    );
  }

  return (
    <TooltipWrapper>
      <ProgressContainer>
        <ProgressBarInner widthPercent={widthPercent}>
          {barLabel}
          {absenceHours > 0 && (
            <AbsenceStripes widthPercent={absencePercentOfBar} />
          )}
        </ProgressBarInner>
      </ProgressContainer>
      <TooltipPopup>
        <TooltipContent>
          <TooltipRow>
            <TooltipLabel>Uppdrag:</TooltipLabel>
            <TooltipValue>
              {getHoursAndMinutesFromMillisecondsString(
                drivingHours * 1000 * 60 * 60
              )}
            </TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Frånvaro:</TooltipLabel>
            <TooltipValue>
              {getHoursAndMinutesFromMillisecondsString(
                absenceHours * 1000 * 60 * 60
              )}
            </TooltipValue>
          </TooltipRow>
          <TooltipRow
            style={{
              borderTop: `1px solid ${theme.colors.border.primary}`,
              paddingTop: '4px',
            }}
          >
            <TooltipLabel>Totalt:</TooltipLabel>
            <TooltipValue>
              {getHoursAndMinutesFromMillisecondsString(
                totalHours * 1000 * 60 * 60
              )}
            </TooltipValue>
          </TooltipRow>
        </TooltipContent>
      </TooltipPopup>
    </TooltipWrapper>
  );
};

const DrivingPolicyOverview = () => {
  const [visibleBusinessUnits, setVisibleBusinessUnits] = useLocalStorage<
    number[]
  >('availability.visibleBUs', []);
  const [searchInput, setSearchInput] = useState<string>('');
  const users = useUsers();
  const { unitsById } = useBusinessUnits();

  const businessUnitOptions = useMemo((): Option<number>[] => {
    return Array.from(unitsById.values()).map((unit) => ({
      label: unit.name,
      value: unit.id,
    }));
  }, [unitsById]);

  const {
    data: reports,
    status,
    refetch,
  } = useDrivingPolicyGetDrivingReports();

  // const handleRowClick = (report: DrivingPolicyReportDto) => {
  //   // console.log('report: ', report);
  // };

  const columnSettings: ColumnSetting<
    DrivingPolicyReportDto,
    TranslationLookups
  >[] = useMemo(() => {
    const columns: ColumnSetting<
      DrivingPolicyReportDto,
      TranslationLookups
    >[] = [
      {
        head: 'Namn',
        css: RowCss,
        render: (report) => users[report.fieldTesterId!]?.name ?? '',
        sortFunction: (a, b) => {
          const aName = users[a.fieldTesterId!]?.name ?? '';
          const bName = users[b.fieldTesterId!]?.name ?? '';
          return aName.localeCompare(bName);
        },
        width: 300,
      },
      {
        head: '24h',
        css: RowCss,
        render: (report) => (
          <FontAwesomeIcon
            icon={
              report.hasConsecutiveHoursOfRestLast24Hours ? faCheck : faWarning
            }
            style={{
              color: report.hasConsecutiveHoursOfRestLast24Hours
                ? 'green'
                : 'red',
            }}
          />
        ),
        sortFunction: (a, b) =>
          a.hasConsecutiveHoursOfRestLast24Hours ===
          b.hasConsecutiveHoursOfRestLast24Hours
            ? 0
            : a.hasConsecutiveHoursOfRestLast24Hours
              ? 1
              : -1,
        width: 40,
      },
      {
        head: '7d',
        css: RowCss,
        render: (report) => (
          <FontAwesomeIcon
            icon={
              report.hasConsecutiveHoursOfRestThisWeek ? faCheck : faWarning
            }
            style={{
              color: report.hasConsecutiveHoursOfRestThisWeek ? 'green' : 'red',
            }}
          />
        ),
        sortFunction: (a, b) =>
          a.hasConsecutiveHoursOfRestThisWeek ===
          b.hasConsecutiveHoursOfRestThisWeek
            ? 0
            : a.hasConsecutiveHoursOfRestThisWeek
              ? 1
              : -1,
        width: 40,
      },
      {
        head: 'Innevarande vecka',
        css: RowCss,
        render: (report) => {
          const value = Math.max(report?.hoursDrivenThisWeek || 0, 0);
          const absenceValue = report?.absenceHoursThisWeek || 0;
          const maxPerWeek = report?.maxHoursPerWeek ?? DEFAULT_MAX_HOURS_PER_WEEK;
          const isFullTime = users[report.fieldTesterId!]?.isFullTime ?? false;
          return getDrivingBarHours(value, absenceValue, isFullTime) ? (
            <ProgressBarWithTooltip
              totalHours={value}
              absenceHours={absenceValue}
              maxHours={maxPerWeek}
              isFullTime={isFullTime}
            />
          ) : (
            '-'
          );
        },
        sortFunction: (a, b) =>
          getDrivingBarHours(
            a.hoursDrivenThisWeek,
            a.absenceHoursThisWeek,
            users[a.fieldTesterId!]?.isFullTime
          ) -
          getDrivingBarHours(
            b.hoursDrivenThisWeek,
            b.absenceHoursThisWeek,
            users[b.fieldTesterId!]?.isFullTime
          ),
        width: 200,
      },
      {
        head: 'Innevarande månad',
        css: RowCss,
        render: (report) => {
          const value = Math.max(report?.hoursDrivenThisMonth || 0, 0);
          const absenceValue = report?.absenceHoursThisMonth || 0;
          const maxPer4Weeks = report?.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          const isFullTime = users[report.fieldTesterId!]?.isFullTime ?? false;
          return getDrivingBarHours(value, absenceValue, isFullTime) ? (
            <ProgressBarWithTooltip
              totalHours={value}
              absenceHours={absenceValue}
              maxHours={maxPer4Weeks}
              isFullTime={isFullTime}
            />
          ) : (
            '-'
          );
        },
        sortFunction: (a, b) =>
          getDrivingBarHours(
            a.hoursDrivenThisMonth,
            a.absenceHoursThisMonth,
            users[a.fieldTesterId!]?.isFullTime
          ) -
          getDrivingBarHours(
            b.hoursDrivenThisMonth,
            b.absenceHoursThisMonth,
            users[b.fieldTesterId!]?.isFullTime
          ),
        width: 200,
      },
      {
        head: 'De senaste 28 dagarna',
        css: RowCss,
        render: (report) => {
          const value = Math.max(report?.hoursDrivenLast28Days || 0, 0);
          const absenceValue = report?.absenceHoursLast28Days || 0;
          const maxPer4Weeks = report?.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          const isFullTime = users[report.fieldTesterId!]?.isFullTime ?? false;
          return getDrivingBarHours(value, absenceValue, isFullTime) ? (
            <ProgressBarWithTooltip
              totalHours={value}
              absenceHours={absenceValue}
              maxHours={maxPer4Weeks}
              isFullTime={isFullTime}
            />
          ) : (
            '-'
          );
        },
        sortFunction: (a, b) =>
          getDrivingBarHours(
            a.hoursDrivenLast28Days,
            a.absenceHoursLast28Days,
            users[a.fieldTesterId!]?.isFullTime
          ) -
          getDrivingBarHours(
            b.hoursDrivenLast28Days,
            b.absenceHoursLast28Days,
            users[b.fieldTesterId!]?.isFullTime
          ),
        width: 200,
      },
      {
        head: 'Kvar senaste 28 dagarna',
        css: RowCss,
        render: (report) => {
          const maxPer4Weeks = report.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          return formatHoursAndMinutesString(
            toHoursAndMinutesString(
              Math.max(0, maxPer4Weeks - (report.hoursDrivenLast28Days ?? 0))
            )
          );
        },
        sortFunction: (a, b) => {
          const aMax = a.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          const bMax = b.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          return (
            aMax -
            (a.hoursDrivenLast28Days ?? 0) -
            (bMax - (b.hoursDrivenLast28Days ?? 0))
          );
        },
        width: 200,
      },
      {
        head: 'Kvar innevarande månad',
        css: RowCss,
        render: (report) => {
          const maxPer4Weeks = report.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          return formatHoursAndMinutesString(
            toHoursAndMinutesString(
              Math.max(0, maxPer4Weeks - (report.hoursDrivenThisMonth ?? 0))
            )
          );
        },
        sortFunction: (a, b) => {
          const aMax = a.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          const bMax = b.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
          return (
            aMax -
            (a.hoursDrivenThisMonth ?? 0) -
            (bMax - (b.hoursDrivenThisMonth ?? 0))
          );
        },
        width: 200,
      },
    ];
    return columns;
  }, [users]);

  const filteredReports = useMemo(() => {
    return (
      reports?.filter((report) => {
        // Externa lastbilstransportörer omfattas inte av körtidspolicyn, så de
        // ska inte listas i körtidspolicy-översikten alls.
        if (users[report.fieldTesterId!]?.isTruckTransport) {
          return false;
        }
        let include = users[report.fieldTesterId!]?.name
          .toLowerCase()
          .includes(searchInput.toLowerCase());
        if (visibleBusinessUnits.length > 0) {
          include =
            include &&
            visibleBusinessUnits.includes(
              users[report.fieldTesterId!].businessUnitID!
            );
        }
        return include;
      }) ?? []
    );
  }, [reports, searchInput, users, visibleBusinessUnits]);

  if (status === 'loading') {
    return <LoadingSpinner />;
  }

  if (status === 'error') {
    return (
      <div>
        <p>Något gick fel, försök igen?</p>
        <Button onClick={() => refetch} type="button">
          Försök igen
        </Button>
      </div>
    );
  }

  return (
    <Wrapper>
      <Actions>
        <Input
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Filtrera på namn"
          value={searchInput}
          width={300}
        />
        <StyledMultiSelect
          onChange={(units) => setVisibleBusinessUnits(Array.from(units))}
          options={businessUnitOptions}
          placeholder="Välj enhet"
          value={new Set(visibleBusinessUnits)}
        />
      </Actions>
      <Table
        columnSettings={columnSettings}
        // onRowClick={handleRowClick}
        rows={filteredReports}
      />
    </Wrapper>
  );
};

export default DrivingPolicyOverview;
